import { useState, useLayoutEffect } from "react";
import { Row, Col } from "react-bootstrap"
import FilterReport from "../header/FilterReport"
import styles from "../style.module.css"
import { format, startOfMonth } from "date-fns"
import axiosService from "../../../utils/axios.service";
import { Table, Spin, Tabs } from "antd"
import "../scss/report.scss"
import StorageService from "../../../utils/storage.service";

const emptySummary = {
    total_book: 0, no_show: 0, cancelled: 0, cancel_rate: 0,
    net_sales: 0, actual_paid: 0, used: 0, buyers: 0, close_rate: 0,
    aov_card: 0, aov_buyer: 0,
}

const money = (value) => new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(value || 0)
const percent = (value) => `${((value || 0) * 100).toFixed(1)}%`
const count = (value) => new Intl.NumberFormat("vi-VN").format(value || 0)

const cardTabs = [
    { key: "net", label: "Doanh số thuần", field: "net", money: true },
    { key: "net_service", label: "Doanh số thuần - Dịch vụ", field: "net_service", money: true },
    { key: "paid", label: "Thực thu", field: "paid", money: true },
    { key: "paid_service", label: "Thực thu - Dịch vụ", field: "paid_service", money: true },
    { key: "lead", label: "Lead", field: "service_customers", money: false },
]

const storeColumnWidth = 108
const fixedColumnWidth = 132 + 200 + 116

const CardMatrix = ({ cards, cardStores, cardTab, setCardTab, subtitle }) => {
    const tab = cardTabs.find((item) => item.key === cardTab) || cardTabs[0]
    const formatValue = tab.money ? money : count
    const qtyField = tab.key === "lead" || tab.field.endsWith("service") ? "service_qty" : "qty"
    const visible = cards.filter((card) => Number(card.total?.[tab.field]) > 0 || Number(card.total?.[qtyField]) > 0)
    const cell = (bucket, isTotal) => {
        const value = Number(bucket?.[tab.field]) || 0
        const qty = Number(bucket?.[qtyField]) || 0
        const active = value > 0 || qty > 0
        return {
            children: (
                <div>
                    <div style={{ fontWeight: active ? 600 : 400, lineHeight: "16px", color: active ? "#1c1917" : "#d3c4b8" }}>{formatValue(value)}</div>
                    <div style={{ fontSize: 11, lineHeight: "14px", color: qty > 0 ? "#a56b4a" : "transparent" }}>{qty > 0 ? count(qty) : "0"}</div>
                </div>
            ),
            props: {
                className: isTotal ? "card-total-cell" : undefined,
                style: { textAlign: "center", background: isTotal ? "#fdeee4" : "#fff" },
            },
        }
    }
    const columns = [
        {
            title: "Nhóm dịch vụ",
            dataIndex: "category_name",
            fixed: "left",
            width: 132,
            ellipsis: true,
        },
        {
            title: "Thẻ dịch vụ",
            dataIndex: "product_name",
            fixed: "left",
            width: 200,
            ellipsis: true,
        },
        {
            title: "Tổng",
            dataIndex: "total",
            fixed: "left",
            width: 116,
            align: "center",
            onHeaderCell: () => ({ className: "card-total-cell" }),
            render: (_, row) => cell(row.total, true),
        },
        ...cardStores.map((store) => ({
            title: <span title={store.name_store}>{store.name_store}</span>,
            key: store.id,
            width: storeColumnWidth,
            align: "center",
            render: (_, row) => cell(row.stores?.[store.id], false),
        })),
    ]
    return (
        <div className="bg-white mt-3 card-service-matrix">
            <div style={{ padding: "14px 16px 0" }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#1c1917" }}>Thẻ dịch vụ</div>
                <div style={{ fontSize: 13, color: "#8d7364" }}>{subtitle}</div>
            </div>
            <Tabs activeKey={cardTab} onChange={setCardTab}>
                {cardTabs.map((item) => <Tabs.TabPane tab={item.label} key={item.key} />)}
            </Tabs>
            <div className="px-2 pb-2">
                <Table
                    columns={columns}
                    dataSource={visible}
                    rowKey="key"
                    size="small"
                    pagination={false}
                    bordered
                    scroll={{ x: fixedColumnWidth + cardStores.length * storeColumnWidth, y: 560 }}
                    locale={{ emptyText: "Không có thẻ dịch vụ trong bộ lọc này" }}
                />
            </div>
        </div>
    )
}

const ReportBusinessCustomer = ({ kind }) => {
    const isCourse = kind === "course"
    const title = isCourse ? "Khách hàng liệu trình" : "Khách hàng mới"
    const today = format(new Date(), "yyyy-MM-dd")
    const [startDate, setStartDate] = useState(isCourse ? today : format(startOfMonth(new Date()), "yyyy-MM-dd"))
    const [endDate, setEndDate] = useState(today)
    const [store, setStore] = useState(0)
    const [channel, setChannel] = useState("all")
    const [rows, setRows] = useState([])
    const [cards, setCards] = useState([])
    const [cardStores, setCardStores] = useState([])
    const [cardTab, setCardTab] = useState("net")
    const [summary, setSummary] = useState(emptySummary)
    const [loading, setLoading] = useState(false)
    const [userAdmin] = useState(JSON.parse(StorageService.getItem("user_info")))

    const fetchData = async () => {
        setLoading(true)
        const res = await axiosService("api/reports/business-customers", "POST", {
            kind: isCourse ? "course" : "new",
            channel,
            store,
            startDate,
            endDate,
        })
        if (res?.data?.code === 200) {
            setSummary(res.data.data?.summary || emptySummary)
            setRows((res.data.data?.stores || []).map((item) => ({ ...item, key: item.store_id })))
            setCards(res.data.data?.cards || [])
            setCardStores(res.data.data?.card_stores || [])
        } else {
            setSummary(emptySummary)
            setRows([])
            setCards([])
            setCardStores([])
        }
        setLoading(false)
    }

    useLayoutEffect(() => {
        fetchData()
    }, [startDate, endDate, store, channel, kind])

    const cancelledTotal = (row) => (Number(row.no_show) || 0) + (Number(row.cancelled) || 0)
    const courseColumns = [
        { title: "Cơ sở", dataIndex: "store_name", fixed: "left", width: 200, ellipsis: true },
        {
            title: "Khách hàng",
            children: [
                { title: "Số lượng KH đặt lịch", dataIndex: "total_book", align: "center", width: 140 },
                { title: "Số lượng KH huỷ lịch", align: "center", width: 140, render: (_, row) => cancelledTotal(row) },
                { title: "Tỷ lệ huỷ lịch", dataIndex: "cancel_rate", align: "center", width: 120, render: percent },
                { title: "KH mua dịch vụ", dataIndex: "buyers", align: "center", width: 130 },
                { title: "Tỷ lệ chốt", dataIndex: "close_rate", align: "center", width: 110, render: percent },
            ],
        },
        { title: "Doanh số thuần", dataIndex: "net_sales", align: "right", width: 150, render: money },
        { title: "Thực thu", dataIndex: "actual_paid", align: "right", width: 150, render: money },
        { title: "AOV (Thẻ dịch vụ)", dataIndex: "aov_card", align: "right", width: 150, render: money },
        { title: "AOV (Khách hàng)", dataIndex: "aov_buyer", align: "right", width: 150, render: money },
    ]

    const metrics = [
        ["Số lượng KH đặt lịch", summary.total_book],
        ["Số lượng KH huỷ lịch", cancelledTotal(summary)],
        ["Tỷ lệ huỷ lịch", percent(summary.cancel_rate)],
        ["KH mua dịch vụ", summary.buyers],
        ["Tỷ lệ chốt", percent(summary.close_rate)],
        ["Doanh số thuần", money(summary.net_sales)],
        ["Thực thu", money(summary.actual_paid)],
        ["AOV (Thẻ dịch vụ)", money(summary.aov_card)],
        ["AOV (Khách hàng)", money(summary.aov_buyer)],
    ]

    return (
        <div className={`${styles.background} container-fluid report-page course-customer-report`}>
            <FilterReport
                startDate={startDate}
                endDate={endDate}
                handleRangeDate={(sDate, eDate) => {
                    setStartDate(sDate)
                    setEndDate(eDate)
                }}
                store={store}
                handleStore={setStore}
                channel={channel}
                handleChannel={setChannel}
                title={title}
                type={isCourse ? "customer-course" : "customer-new"}
                userAdmin={userAdmin}
            />
            <Spin spinning={loading}>
                <Row className="mt-3 mb-3">
                    {metrics.map(([label, value], index) => (
                        <Col key={label} xs={6} md={4} lg={2} className="mb-2">
                            <div className={`course-kpi${index === 0 ? " is-primary" : ""}`}>
                                <div className="course-kpi-label">{label}</div>
                                <strong>{value}</strong>
                            </div>
                        </Col>
                    ))}
                </Row>
                <div className="course-panel">
                <Table
                    className="course-grid"
                    columns={courseColumns}
                    dataSource={rows}
                    pagination={false}
                    bordered
                    tableLayout="fixed"
                    scroll={{ x: 1440 }}
                    locale={{ emptyText: "Không có dữ liệu trong bộ lọc này" }}
                />
                </div>
                <CardMatrix
                    cards={cards}
                    cardStores={cardStores}
                    cardTab={cardTab}
                    setCardTab={setCardTab}
                    subtitle={title}
                />
            </Spin>
        </div>
    )
}

export default ReportBusinessCustomer
